import { useState } from "react";
import {
    Box, Typography, Grid, Avatar, Chip, useMediaQuery, Tabs, Tab
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { AiFillBulb } from "react-icons/ai";

const PageWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    background: "linear-gradient(160deg, #03020a 0%, #060c18 50%, #03050f 100%)",
    color: "#fff",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    paddingBottom: "60px",
    paddingTop: "32px",
}));

const SectionCard = styled(Box)(() => ({
    background: "rgba(26,14,52,0.95)",
    border: "1px solid rgba(218,165,32,0.25)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "28px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "10%", right: "10%", height: "1px",
        background: "linear-gradient(to right,transparent,rgba(218,165,32,0.3),transparent)",
    },
}));

const MobileDataCard = styled(Box)(() => ({
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(218,165,32,0.15)",
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "10px",
}));

const CodeBlock = styled(Box)(() => ({
    background: "rgba(6,2,16,0.98)",
    border: "1px solid rgba(218,165,32,0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.9,
    overflowX: "auto",
    whiteSpace: "pre",
    color: "#e6edf3",
    position: "relative",
    "&::before": {
        content: '"C++"',
        position: "absolute",
        top: "8px", right: "14px",
        fontSize: "9px",
        color: "rgba(218,165,32,0.5)",
        letterSpacing: "2px",
        fontWeight: 700,
    },
}));

const TableHeader = ({ columns, cols }) => (
    <Box sx={{
        display: "grid",
        gridTemplateColumns: cols || `repeat(${columns.length},1fr)`,
        gap: "12px", padding: "12px 16px",
        bgcolor: "rgba(218,165,32,0.1)", color: "goldenrod",
        fontWeight: "bold", fontSize: "0.78rem",
        letterSpacing: "0.06em", textTransform: "uppercase",
        borderBottom: "1px solid rgba(218,165,32,0.15)",
    }}>
        {columns.map(c => <div key={c}>{c}</div>)}
    </Box>
);

const SectionHeading = ({ icon, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 4, mb: subtitle ? 1 : 2 }}>
        <Avatar sx={{ bgcolor: "goldenrod", color: "#100720", width: 38, height: 38, flexShrink: 0, mt: 0.3 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{subtitle}</Typography>}
        </Box>
    </Box>
);

const ConceptCard = ({ title, color = "goldenrod", children }) => (
    <Box sx={{
        bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`, borderRadius: "12px",
        p: "16px 18px", mb: 2,
    }}>
        {title && <Typography sx={{ fontWeight: 700, color, fontSize: "0.82rem", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</Typography>}
        {children}
    </Box>
);

const StyledTabs = ({ value, onChange, tabs }) => (
    <Tabs value={value} onChange={onChange} variant="scrollable" scrollButtons="auto"
          sx={{
              mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)",
              "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 },
              "& .Mui-selected": { color: "goldenrod !important" },
              "& .MuiTabs-indicator": { bgcolor: "goldenrod" },
          }}>
        {tabs.map((t, i) => <Tab key={i} label={t} />)}
    </Tabs>
);

const PillChip = ({ label, color = "goldenrod" }) => (
    <Chip label={label} size="small" sx={{
        bgcolor: `${color}18`, color, fontWeight: 700, fontSize: "0.72rem",
        height: 22, border: `1px solid ${color}44`, "& .MuiChip-label": { px: 1 },
    }} />
);
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 1: Syntax & Anatomy
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const syntaxTabs = [
    {
        label: "Basic Syntax",
        code: `#include <iostream>
#include <functional>

// ── Lambda anatomy ────────────────────────────────────────────
//   [capture](parameters) -> return_type { body }
//    ───┬───  ─────┬─────   ──────┬──────  ──┬──
//       │          │              │            └── function body
//       │          │              └── optional return type
//       │          └── parameters (like a regular function)
//       └── capture list: what from outer scope to bring in

// ── Minimal lambda ────────────────────────────────────────────
auto hello = []() { std::cout << "Hello!\\n"; };
hello();                          // call it like a function

// ── Lambda with parameters ────────────────────────────────────
auto add = [](int a, int b) { return a + b; };
std::cout << add(3, 4) << "\\n";  // 7

// ── Explicit return type ──────────────────────────────────────
auto divide = [](double a, double b) -> double {
    if (b == 0) return 0.0;
    return a / b;
};

// ── No capture, no params ──────────────────────────────────────
auto greet = [] { std::cout << "Hi!\\n"; };   // () optional when no params
greet();

// ── Calling immediately (IIFE) ────────────────────────────────
int result = [](int x, int y){ return x * y; }(6, 7);   // 42
// Useful for complex initialisation in const contexts

// ── Store in std::function ────────────────────────────────────
std::function<int(int, int)> fn = [](int a, int b){ return a + b; };
std::cout << fn(10, 20) << "\\n";   // 30

// ── auto is cleaner for storing lambdas ───────────────────────
auto fn2 = [](int a, int b){ return a + b; };   // preferred`,
    },
    {
        label: "Return Type Deduction",
        code: `#include <iostream>
#include <string>

// ── Return type is usually inferred automatically ─────────────
auto square = [](int x) { return x * x; };    // deduced: int
auto half   = [](double x) { return x / 2; }; // deduced: double
auto msg    = []{ return std::string("hi"); }; // deduced: std::string

// ── Explicit return type — required when: ─────────────────────
// 1. Multiple return statements with different types
auto classify = [](int n) -> std::string {
    if (n < 0) return "negative";    // string literal
    if (n > 0) return std::string("positive");  // std::string
    return "zero";
    // Without -> std::string: compiler might complain about mixed types
};

// 2. Returning a reference
int value = 42;
auto getRef = [&value]() -> int& { return value; };
getRef() = 99;                         // modifies value through reference
std::cout << value << "\\n";           // 99

// 3. Trailing return with complex expressions
auto clamp = [](double v, double lo, double hi) -> double {
    return v < lo ? lo : (v > hi ? hi : v);
};
std::cout << clamp(15.0, 0.0, 10.0) << "\\n";  // 10.0

// ── void return (explicit or implicit) ────────────────────────
auto doNothing = []() -> void {};
auto doNothing2 = []() {};             // same — void inferred`,
    },
    {
        label: "Generic Lambdas (C++14)",
        code: `#include <iostream>
#include <vector>
#include <string>
#include <algorithm>

// ── auto parameters = generic lambda (C++14) ──────────────────
// Compiler generates a templated operator() for each call type

auto identity = [](auto x) { return x; };
identity(42);           // int version
identity(3.14);         // double version
identity("hello");      // const char* version

// ── Multiple auto parameters ──────────────────────────────────
auto add = [](auto a, auto b) { return a + b; };
add(1, 2);              // int + int  → int
add(1.5, 2.5);          // double + double → double
add(std::string("hi"), std::string(" there")); // string concat

// ── Generic comparator ────────────────────────────────────────
auto byLength = [](const auto& a, const auto& b) {
    return a.size() < b.size();
};
std::vector<std::string> words = {"banana","fig","apple","kiwi"};
std::sort(words.begin(), words.end(), byLength);
// {"fig","kiwi","apple","banana"}

// ── Generic transform ─────────────────────────────────────────
auto print = [](const auto& container) {
    for (const auto& item : container)
        std::cout << item << ' ';
    std::cout << "\\n";
};
print(words);
print(std::vector<int>{1,2,3,4,5});

// ── C++20: explicit template parameters in lambdas ────────────
auto typedIdentity = []<typename T>(T x) -> T { return x; };
// Allows constraints: []<typename T> requires std::integral<T>(T x){...}`,
    },
    {
        label: "Mutable Lambda",
        code: `#include <iostream>

// ── By default, captured copies are CONST inside a lambda ─────
int count = 0;
auto badCounter = [count]() {
    // count++;   // ❌ ERROR: count is captured by value — it's const!
    return count;
};

// ── mutable removes the const from captured copies ────────────
auto counter = [count]() mutable {
    ++count;              // ✅ OK — mutable allows modifying the copy
    return count;
};

std::cout << counter() << "\\n";  // 1
std::cout << counter() << "\\n";  // 2  (lambda has its own copy of count)
std::cout << counter() << "\\n";  // 3
std::cout << count     << "\\n";  // 0  ← original unchanged!

// ── mutable with reference capture ───────────────────────────
// [&] captures by reference — already mutable, no keyword needed
auto refCounter = [&count]() {
    ++count;              // modifies the ORIGINAL count
    return count;
};
refCounter();  refCounter();
std::cout << count << "\\n";   // 2 — original WAS modified

// ── Stateful lambda: mutable lets it remember state ───────────
auto makeCounter = [](int start) {
    return [n = start]() mutable { return ++n; };  // captures n by value
};
auto c1 = makeCounter(10);
auto c2 = makeCounter(0);
std::cout << c1() << c1() << c1() << "\\n";  // 11 12 13
std::cout << c2() << c2() << "\\n";           // 1 2  (independent)`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 2: Capture Modes
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const captureTabs = [
    {
        label: "Capture by Value",
        code: `#include <iostream>
#include <string>

// ── Capture by VALUE — copies the variable at creation time ───
int x = 10, y = 20;
auto sumXY = [x, y]() { return x + y; };   // copies 10 and 20

x = 999;   // change original AFTER capture
y = 999;

std::cout << sumXY() << "\\n";   // still 30 — copies were taken at capture!

// ── [=] — capture ALL used variables by value ─────────────────
std::string name = "Alice";
int age = 30;

auto greet = [=]() {
    std::cout << "Hello " << name << ", age " << age << "\\n";
};
name = "Bob";   // change originals
age  = 99;
greet();        // Hello Alice, age 30 — copies from capture time

// ── Capture specific + default ────────────────────────────────
int a = 1, b = 2, c = 3;
auto f = [=, &c]() {    // a,b by value; c by reference
    // a and b are copies, c is a reference
    c += a + b;         // modifies original c
};
f();
std::cout << c << "\\n";   // 6

// ── Capture with initialiser (C++14) ──────────────────────────
// Create a new variable in the capture list:
auto offset = [base = 100](int n) { return n + base; };
std::cout << offset(5) << "\\n";   // 105

// Move into lambda (capture by move):
auto ptr = std::make_unique<int>(42);
auto usesPtr = [p = std::move(ptr)]() { return *p; };
// ptr is now null — ownership transferred to lambda`,
    },
    {
        label: "Capture by Reference",
        code: `#include <iostream>
#include <vector>
#include <numeric>

// ── Capture by REFERENCE — alias to the original variable ─────
int total = 0;
auto accumulate = [&total](int n) { total += n; };

accumulate(10);
accumulate(20);
accumulate(30);
std::cout << total << "\\n";   // 60 — original total was modified

// ── [&] — capture ALL used variables by reference ────────────
std::vector<int> results;
int multiplier = 3;

auto process = [&](int x) {
    results.push_back(x * multiplier);   // both captured by ref
};
process(5);   // appends 15
process(7);   // appends 21
multiplier = 10;
process(2);   // appends 20  ← sees NEW multiplier value!

// ── Dangerous: capturing by ref when lambda outlives scope ───
auto makeAdder = [](int base) {
    // int localBase = base;
    // return [&localBase](int x){ return x + localBase; };
    //  ↑ DANGER! localBase destroyed when makeAdder returns → DANGLING REF

    return [base](int x){ return x + base; };  // ✅ capture by VALUE
};

// ── [&] vs [=] rule of thumb ──────────────────────────────────
// ✅ [&] — when lambda called in SAME scope (e.g. algorithm callback)
// ✅ [=] — when lambda might outlive its creation scope (stored, returned)
// ✅ Mix: [=, &output] — most things by value, output by ref
// ❌ [&] for stored/returned lambdas — dangling reference risk!`,
    },
    {
        label: "this Capture",
        code: `#include <iostream>
#include <functional>
#include <string>

class Button {
    std::string label;
    int clickCount = 0;
public:
    Button(const std::string& l) : label(l) {}

    // ── Capture [this] — access class members ─────────────────
    std::function<void()> getClickHandler() {
        return [this]() {
            ++clickCount;                 // access member via this
            std::cout << label << " clicked " << clickCount << " times\\n";
        };
    }

    // ── Capture [=] in C++17 also captures this ───────────────
    std::function<void()> getLogger() {
        return [=]() {                    // [=] implies [this] capture
            std::cout << "Log: " << label << "\\n";
        };
    }

    // ── C++17: capture [*this] — copies the ENTIRE object ─────
    std::function<void()> getSafeHandler() {
        return [*this]() {               // copies Button object!
            std::cout << label << " (safe copy)\\n";
            // Modifying clickCount here would NOT affect original
        };
    }

    // ── [this] in member lambdas is common ────────────────────
    void processAll(std::vector<int>& data) {
        std::transform(data.begin(), data.end(), data.begin(),
            [this](int x) { return transform(x); }
        );
    }

    int transform(int x) { return x * 2; }
};

int main() {
    Button btn("OK");
    auto handler = btn.getClickHandler();
    handler();   // OK clicked 1 times
    handler();   // OK clicked 2 times
}`,
    },
    {
        label: "Capture Best Practices",
        code: `#include <iostream>
#include <functional>
#include <memory>

// ── Capture checklist ─────────────────────────────────────────

// ✅ 1. Be explicit — name what you capture
int x = 10, y = 20, z = 30;
auto f1 = [x, y]() { return x + y; };   // clear what's captured
// auto f2 = [=]() { return x + y; };   // ⚠️ unclear — captures z too!

// ✅ 2. Prefer value for stored/returned lambdas
auto makeGreeter = [](std::string name) {
    return [name]() { std::cout << "Hi " << name << "!\\n"; };  // copy
};

// ✅ 3. Use [&] only in same-scope algorithms
std::vector<int> v = {1,2,3,4,5};
int total = 0;
std::for_each(v.begin(), v.end(), [&total](int n){ total += n; });
// ↑ Lambda called synchronously in this scope — [&] is safe here

// ✅ 4. Move expensive objects into lambda (C++14 init capture)
auto largeData = std::make_unique<std::vector<int>>(1000000, 1);
auto process = [data = std::move(largeData)]() {
    std::cout << "Data size: " << data->size() << "\\n";
};   // largeData transferred — no copy of 1M elements!

// ✅ 5. [*this] for async member callbacks (C++17)
struct Worker {
    int id;
    std::function<void()> makeTask() {
        return [*this]() {               // safe even if Worker is destroyed
            std::cout << "Task from " << id << "\\n";
        };
    }
};

// ❌ Don't capture everything with [&] in async/stored lambdas:
// auto bad = [&]() { use(x); use(y); };  // if stored, x/y may be dead!`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 3: Lambdas with STL Algorithms
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const stlTabs = [
    {
        label: "Sorting & Searching",
        code: `#include <algorithm>
#include <vector>
#include <string>
#include <iostream>

std::vector<int> nums = {5, 2, 8, 1, 9, 3, 7, 4, 6};

// ── std::sort with lambda comparator ──────────────────────────
std::sort(nums.begin(), nums.end(),
    [](int a, int b) { return a < b; });     // ascending
std::sort(nums.begin(), nums.end(),
    [](int a, int b) { return a > b; });     // descending

// ── Sort structs by field ──────────────────────────────────────
struct Person { std::string name; int age; };
std::vector<Person> people = {{"Alice",30},{"Bob",25},{"Carol",35}};

std::sort(people.begin(), people.end(),
    [](const Person& a, const Person& b) { return a.age < b.age; });
// Sorted: Bob(25), Alice(30), Carol(35)

// ── Multi-field sort ──────────────────────────────────────────
std::sort(people.begin(), people.end(),
    [](const Person& a, const Person& b) {
        if (a.age != b.age) return a.age < b.age;  // primary: age
        return a.name < b.name;                     // secondary: name
    });

// ── std::find_if ──────────────────────────────────────────────
auto it = std::find_if(nums.begin(), nums.end(),
    [](int n) { return n > 7; });           // first element > 7
if (it != nums.end())
    std::cout << "Found: " << *it << "\\n";

// ── std::lower_bound / upper_bound with lambda ────────────────
std::sort(nums.begin(), nums.end());
auto lb = std::lower_bound(nums.begin(), nums.end(), 5,
    [](int val, int target) { return val < target; });`,
    },
    {
        label: "Transform & Filter",
        code: `#include <algorithm>
#include <vector>
#include <string>
#include <numeric>

std::vector<int> v = {1, 2, 3, 4, 5, 6, 7, 8, 9, 10};

// ── std::transform — map each element ────────────────────────
std::vector<int> squares(v.size());
std::transform(v.begin(), v.end(), squares.begin(),
    [](int n) { return n * n; });
// squares = {1, 4, 9, 16, 25, 36, 49, 64, 81, 100}

// ── std::transform with two ranges ───────────────────────────
std::vector<int> a = {1,2,3}, b = {10,20,30};
std::vector<int> sums(3);
std::transform(a.begin(), a.end(), b.begin(), sums.begin(),
    [](int x, int y) { return x + y; });
// sums = {11, 22, 33}

// ── Erase-remove idiom — filter out elements ──────────────────
auto isEven = [](int n) { return n % 2 == 0; };
v.erase(std::remove_if(v.begin(), v.end(), isEven), v.end());
// v = {1, 3, 5, 7, 9}

// ── std::copy_if — copy matching elements ─────────────────────
std::vector<int> evens;
std::copy_if(v.begin(), v.end(), std::back_inserter(evens),
    [](int n) { return n % 2 == 0; });

// ── std::accumulate with lambda reducer ──────────────────────
int product = std::accumulate(v.begin(), v.end(), 1,
    [](int acc, int n) { return acc * n; });  // factorial-like

// ── std::count_if ─────────────────────────────────────────────
int evenCount = std::count_if(v.begin(), v.end(),
    [](int n) { return n % 2 == 0; });`,
    },
    {
        label: "for_each & Predicates",
        code: `#include <algorithm>
#include <vector>
#include <iostream>
#include <string>

std::vector<std::string> words = {"apple","banana","cherry","date","elderberry"};

// ── std::for_each ─────────────────────────────────────────────
std::for_each(words.begin(), words.end(),
    [](const std::string& s) { std::cout << s << "\\n"; });

// ── for_each with capture — accumulate state ──────────────────
size_t totalLen = 0;
std::for_each(words.begin(), words.end(),
    [&totalLen](const std::string& s) { totalLen += s.size(); });
std::cout << "Total chars: " << totalLen << "\\n";  // 30

// ── std::all_of / any_of / none_of ────────────────────────────
std::vector<int> nums = {2, 4, 6, 8, 10};
bool allEven  = std::all_of(nums.begin(), nums.end(),
                    [](int n){ return n % 2 == 0; });   // true
bool anyOver5 = std::any_of(nums.begin(), nums.end(),
                    [](int n){ return n > 5; });         // true
bool noneNeg  = std::none_of(nums.begin(), nums.end(),
                    [](int n){ return n < 0; });         // true

// ── std::partition — split by predicate ──────────────────────
auto mid = std::partition(nums.begin(), nums.end(),
    [](int n){ return n > 5; });
// Elements > 5 come first, <= 5 come after mid

// ── std::max_element / min_element with comparator ────────────
auto longest = std::max_element(words.begin(), words.end(),
    [](const std::string& a, const std::string& b){
        return a.size() < b.size();
    });
std::cout << "Longest: " << *longest << "\\n";   // elderberry`,
    },
    {
        label: "Ranges (C++20)",
        code: `#include <algorithm>
#include <ranges>
#include <vector>
#include <iostream>

std::vector<int> v = {1, 2, 3, 4, 5, 6, 7, 8, 9, 10};

// ── C++20 ranges — no begin()/end() needed ────────────────────
namespace rv = std::views;

// Sort directly on the range:
std::ranges::sort(v, [](int a, int b){ return a > b; });

// ── Composable views with | operator ──────────────────────────
// Filter + transform pipeline (lazy — no copies!):
auto result = v
    | rv::filter([](int n){ return n % 2 == 0; })    // keep evens
    | rv::transform([](int n){ return n * n; });      // square them

for (int x : result)
    std::cout << x << ' ';  // 100 64 36 16 4  (lazy, reversed)

// ── views::take and views::drop ───────────────────────────────
auto first3 = v | rv::take(3);
auto after2 = v | rv::drop(2);

// ── views::iota — generate range ─────────────────────────────
auto squares = rv::iota(1, 11)
    | rv::transform([](int n){ return n * n; });
// {1, 4, 9, 16, 25, 36, 49, 64, 81, 100}  — no vector allocation!

// ── ranges::find_if ───────────────────────────────────────────
auto it = std::ranges::find_if(v, [](int n){ return n > 7; });

// ── ranges::count_if ─────────────────────────────────────────
long count = std::ranges::count_if(v, [](int n){ return n % 3 == 0; });`,
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 4: std::function & Higher-Order Functions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const higherTabs = [
    {
        label: "std::function",
        code: `#include <functional>
#include <iostream>
#include <vector>
#include <string>

// ── std::function<Ret(Args...)> — type-erased callable wrapper ─
// Stores ANY callable: lambda, function pointer, functor, bind

// ── Type signature: return type + parameter types ─────────────
std::function<int(int, int)>   add  = [](int a, int b){ return a+b; };
std::function<void(int)>       log  = [](int n){ std::cout << n; };
std::function<bool(const std::string&)> isLong = [](const std::string& s){
    return s.size() > 5;
};

// ── Also wraps regular function pointers ──────────────────────
int multiply(int a, int b) { return a * b; }
std::function<int(int,int)> fn = multiply;   // function pointer
fn = [](int a, int b){ return a + b; };      // swap to lambda at runtime!

// ── Storing in containers ─────────────────────────────────────
std::vector<std::function<void(int)>> handlers;
handlers.push_back([](int x){ std::cout << "A: " << x << "\\n"; });
handlers.push_back([](int x){ std::cout << "B: " << x*2 << "\\n"; });
handlers.push_back([](int x){ std::cout << "C: " << x*x << "\\n"; });
for (auto& h : handlers) h(5);   // A:5  B:10  C:25

// ── Check if std::function has a target ───────────────────────
std::function<void()> empty;
if (!empty) std::cout << "No function assigned\\n";

// ── std::function cost: heap allocation + virtual dispatch ────
// Prefer auto for local lambdas. Use std::function for:
//   - Storage in containers
//   - Function parameters accepting any callable
//   - Callbacks stored across scope boundaries`,
    },
    {
        label: "Higher-Order Functions",
        code: `#include <functional>
#include <iostream>
#include <vector>
#include <string>

// ── Higher-order function: takes or returns a function ────────

// ── 1. Accept any callable as parameter ───────────────────────
template<typename Fn>
void applyToEach(std::vector<int>& v, Fn func) {  // template = most efficient
    for (auto& x : v) x = func(x);
}

// Or with std::function (less efficient but erases the type):
void applyToEachDyn(std::vector<int>& v, std::function<int(int)> fn) {
    for (auto& x : v) x = fn(x);
}

// ── 2. Return a function (closure factory) ────────────────────
auto makeMultiplier = [](int factor) {
    return [factor](int x){ return x * factor; };
};
auto triple = makeMultiplier(3);
auto double_ = makeMultiplier(2);
std::cout << triple(7) << "\\n";    // 21
std::cout << double_(7) << "\\n";   // 14

// ── 3. Function composition ────────────────────────────────────
template<typename F, typename G>
auto compose(F f, G g) {
    return [f, g](auto x) { return f(g(x)); };  // f(g(x))
}
auto addOne   = [](int x){ return x + 1; };
auto square   = [](int x){ return x * x; };
auto squareThenAddOne = compose(addOne, square);
std::cout << squareThenAddOne(4) << "\\n";  // (4*4)+1 = 17

// ── 4. Memoization ────────────────────────────────────────────
auto memoize = [](auto fn) {
    std::map<int, int> cache;
    return [fn, cache](int n) mutable -> int {
        auto it = cache.find(n);
        if (it != cache.end()) return it->second;
        return cache[n] = fn(n);
    };
};`,
    },
    {
        label: "std::bind vs Lambda",
        code: `#include <functional>
#include <iostream>
#include <vector>
#include <algorithm>

// ── std::bind (C++11) — partial application ────────────────────
void printWithSeparator(const std::string& sep, int n) {
    std::cout << n << sep;
}

using namespace std::placeholders;  // for _1, _2, ...

auto printComma = std::bind(printWithSeparator, ", ", _1);  // fix sep
printComma(42);   // "42, "

// ── Lambda is clearer and preferred in modern C++ ─────────────
auto printCommaLambda = [](int n){ printWithSeparator(", ", n); };

// ── Comparison: std::bind vs lambda ───────────────────────────
struct Multiplier { int factor; int apply(int x) const { return x * factor; } };
Multiplier m{5};

// bind to member function:
auto boundMember = std::bind(&Multiplier::apply, &m, _1);
// Same as lambda:
auto lambdaMember = [&m](int x){ return m.apply(x); };   // clearer!

// ── Partial application with lambda ───────────────────────────
auto greaterThan = [](int threshold, int value){ return value > threshold; };
// Partial: fix threshold = 5
auto greaterThan5 = [](int v){ return greaterThan(5, v); };
// Or use a factory lambda:
auto makeThresholdCheck = [](int threshold){
    return [threshold](int value){ return value > threshold; };
};
auto gt5 = makeThresholdCheck(5);

// ── When to still use std::bind ───────────────────────────────
// bind is verbose and confusing with multiple placeholders.
// Use lambdas instead. bind is mostly legacy C++11 code.
std::vector<int> v = {1,3,6,8,2};
std::sort(v.begin(), v.end(), std::greater<int>());    // still useful for standard functors`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 5: Advanced Patterns
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const advancedTabs = [
    {
        label: "Recursive Lambda",
        code: `#include <iostream>
#include <functional>

// ── A lambda can't call itself by name directly ───────────────
// auto fib = [](int n){ return fib(n-1)+fib(n-2); };  // ❌ fib not in scope yet!

// ── Method 1: std::function (simple but has overhead) ─────────
std::function<int(int)> fib = [&fib](int n) -> int {
    if (n <= 1) return n;
    return fib(n-1) + fib(n-2);   // captures fib by reference
};
std::cout << fib(10) << "\\n";   // 55

// ── Method 2: pass lambda to itself (Y-combinator style) ──────
auto factorial = [](auto& self, int n) -> int {
    return n <= 1 ? 1 : n * self(self, n-1);
};
std::cout << factorial(factorial, 5) << "\\n";   // 120

// ── Method 3: C++23 deducing this (cleanest) ─────────────────
// auto fib23 = [](this auto& self, int n) -> int {
//     return n <= 1 ? n : self(n-1) + self(n-2);
// };

// ── Method 4: helper struct ────────────────────────────────────
struct Fib {
    int operator()(int n) const {
        return n <= 1 ? n : (*this)(n-1) + (*this)(n-2);
    }
};
auto fibHelper = Fib{};
std::cout << fibHelper(10) << "\\n";   // 55

// ── Tail-recursive with accumulator ───────────────────────────
auto factTail = [](auto& self, int n, int acc = 1) -> int {
    return n <= 1 ? acc : self(self, n-1, acc * n);
};
std::cout << factTail(factTail, 10) << "\\n";   // 3628800`,
    },
    {
        label: "Lambda as Callback",
        code: `#include <iostream>
#include <functional>
#include <vector>
#include <string>

// ── Event system with lambda callbacks ────────────────────────
class EventEmitter {
    std::vector<std::function<void(const std::string&)>> listeners;
public:
    void on(std::function<void(const std::string&)> cb) {
        listeners.push_back(std::move(cb));
    }
    void emit(const std::string& event) {
        for (auto& cb : listeners) cb(event);
    }
};

// ── Timer / deferred execution ────────────────────────────────
class Timer {
    std::function<void()> callback;
    int delayMs;
public:
    Timer(int ms, std::function<void()> cb)
        : delayMs(ms), callback(std::move(cb)) {}
    void fire() { callback(); }  // in real code: after delay
};

// ── Fluent builder with lambda customisation ──────────────────
class Request {
    std::string url;
    std::function<void(int, std::string)> onSuccess;
    std::function<void(std::string)>      onError;
public:
    Request& setUrl(const std::string& u) { url = u; return *this; }
    Request& then(std::function<void(int, std::string)> cb) {
        onSuccess = std::move(cb); return *this;
    }
    Request& catch_(std::function<void(std::string)> cb) {
        onError = std::move(cb); return *this;
    }
    void send() {
        // ... make request ...
        if (onSuccess) onSuccess(200, "OK");   // simulate
    }
};

int main() {
    EventEmitter emitter;
    emitter.on([](const std::string& e){ std::cout << "Handler 1: " << e << "\\n"; });
    emitter.on([](const std::string& e){ std::cout << "Handler 2: " << e << "\\n"; });
    emitter.emit("click");

    Request req;
    req.setUrl("https://api.example.com/data")
       .then([](int code, std::string body){ std::cout << code << ": " << body; })
       .catch_([](std::string err){ std::cerr << "Error: " << err; })
       .send();
}`,
    },
    {
        label: "Immediately Invoked & IIFE",
        code: `#include <iostream>
#include <vector>
#include <algorithm>
#include <string>

// ── IIFE — Immediately Invoked Function Expression ────────────
// Useful for complex initialisation of const variables

const int result = []{
    int sum = 0;
    for (int i = 1; i <= 100; ++i) sum += i;
    return sum;
}();   // ← () calls immediately
std::cout << result << "\\n";   // 5050

// ── Complex const initialisation ─────────────────────────────
const std::vector<int> sorted = []{
    std::vector<int> v = {5,3,8,1,9,2,7,4,6};
    std::sort(v.begin(), v.end());
    return v;
}();   // sorted is const but was built procedurally

// ── IIFE with capture ──────────────────────────────────────────
std::string input = "  hello world  ";
const std::string trimmed = [&input]{
    size_t start = input.find_first_not_of(' ');
    size_t end   = input.find_last_not_of(' ');
    return (start == std::string::npos) ? "" : input.substr(start, end-start+1);
}();
std::cout << "[" << trimmed << "]\\n";   // [hello world]

// ── IIFE for switch without named function ────────────────────
int code = 2;
std::string label = [code]() -> std::string {
    switch (code) {
        case 1: return "one";
        case 2: return "two";
        case 3: return "three";
        default: return "other";
    }
}();
std::cout << label << "\\n";   // "two"`,
    },
    {
        label: "Constexpr Lambda (C++17)",
        code: `#include <iostream>
#include <array>

// ── constexpr lambda: evaluated at compile time ───────────────
constexpr auto square = [](int x) constexpr { return x * x; };

// Used in compile-time context:
constexpr int sq5 = square(5);           // 25 at compile time
static_assert(sq5 == 25);               // verified at compile time!

// ── std::array with constexpr lambda ─────────────────────────
constexpr auto makeSquares = []() constexpr {
    std::array<int, 10> arr{};
    for (int i = 0; i < 10; ++i)
        arr[i] = (i+1) * (i+1);
    return arr;
};
constexpr auto squares = makeSquares();  // computed at compile time
static_assert(squares[4] == 25);

// ── constexpr factorial ───────────────────────────────────────
constexpr auto factorial = [](auto& self, int n) constexpr -> int {
    return n <= 1 ? 1 : n * self(self, n-1);
};
constexpr int f5 = factorial(factorial, 5);  // 120, compile time
static_assert(f5 == 120);

// ── C++20: consteval lambda ────────────────────────────────────
// consteval forces compile-time evaluation (not just allowed):
// consteval auto ceSquare = [](int x){ return x*x; };
// int runtime_val = 5;
// ceSquare(runtime_val);  // ❌ ERROR — must be compile-time!

// ── When to use constexpr lambdas ────────────────────────────
// ✅ Lookup tables built at compile time
// ✅ Compile-time maths (factorial, Fibonacci, powers)
// ✅ Template metaprogramming helpers
// ✅ Unit tests verified at compile time (static_assert)`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// REFERENCE TABLES & CHEATSHEET DATA
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const captureRows = [
    { syntax: "[]",          desc: "Capture nothing — no outer variables accessible",         example: "[] { return 42; }" },
    { syntax: "[=]",         desc: "Capture all used vars by VALUE (copies at creation time)", example: "[=]{ return x+y; }" },
    { syntax: "[&]",         desc: "Capture all used vars by REFERENCE (aliases originals)",   example: "[&]{ total += n; }" },
    { syntax: "[x]",         desc: "Capture x by value only",                                  example: "[x]{ return x*2; }" },
    { syntax: "[&x]",        desc: "Capture x by reference only",                              example: "[&x]{ x++; }" },
    { syntax: "[=, &x]",     desc: "All by value EXCEPT x (by ref)",                          example: "[=,&out]{ out=x+y; }" },
    { syntax: "[&, x]",      desc: "All by reference EXCEPT x (by value)",                    example: "[&, id]{ log(id); }" },
    { syntax: "[this]",      desc: "Capture enclosing class's this pointer",                   example: "[this]{ doMember(); }" },
    { syntax: "[*this]",     desc: "Capture copy of entire enclosing object (C++17)",         example: "[*this]{ return id; }" },
    { syntax: "[x=expr]",    desc: "Init-capture: create new var x from expr (C++14)",        example: "[p=move(ptr)]{ *p; }" },
];

const cheatBlocks = [
    {
        title: "Syntax Rules", color: "#f0883e",
        items: [
            "✅ [capture](params) -> RetType { body }",
            "✅ Return type can be omitted — usually deduced",
            "✅ () can be omitted when no parameters",
            "✅ auto stores a lambda (not std::function — faster)",
            "✅ Generic: (auto x) makes a templated lambda (C++14)",
            "❌ Lambda type is unique — can't spell it, only deduce it",
            "❌ Can't store different lambdas in same auto variable",
            "❌ mutable needed to modify captured-by-value copies",
        ],
    },
    {
        title: "Capture Rules", color: "#58a6ff",
        items: [
            "✅ [=] copies — lambda keeps working after scope ends",
            "✅ [&] aliases — changes visible, dangling risk if lambda outlives scope",
            "✅ [x = move(obj)] — move expensive objects in (C++14)",
            "✅ [*this] — safe copy of object for async callbacks (C++17)",
            "✅ Name captured vars explicitly when possible ([x, y] over [=])",
            "❌ Never [&] in stored/returned lambdas — dangling references!",
            "❌ Don't capture pointer to local var — same dangling risk",
            "❌ [=] in member function implicitly captures this",
        ],
    },
    {
        title: "std::function vs auto", color: "#7ee787",
        items: [
            "✅ auto is preferred for local lambdas — zero overhead",
            "✅ std::function for storing in containers or class members",
            "✅ std::function for function parameters accepting any callable",
            "✅ Template param (typename Fn) is faster than std::function",
            "❌ std::function incurs heap allocation + virtual dispatch",
            "❌ Don't use std::function in tight loops — overhead matters",
            "❌ Can't store different lambda types in same auto var",
        ],
    },
    {
        title: "Common Mistakes", color: "#f85149",
        items: [
            "❌ [&] in returned or stored lambda → dangling reference",
            "❌ Forgetting mutable when modifying by-value captures",
            "❌ Recursion: lambda can't call itself by name directly",
            "❌ Calling virtual functions via [this] in constructor",
            "❌ Capturing large objects by value in hot-path lambdas",
            "❌ Assuming [=] captures all vars (only USED ones)",
            "❌ Using std::bind when a lambda is clearer",
            "❌ Forgetting -> RetType when returning different types",
        ],
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function CppLambdaExpressions() {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [synTab,    setSynTab]    = useState(0);
    const [capTab,    setCapTab]    = useState(0);
    const [stlTab,    setStlTab]    = useState(0);
    const [hiTab,     setHiTab]     = useState(0);
    const [advTab,    setAdvTab]    = useState(0);

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Box sx={{ textAlign: "center", mb: 5, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{
                    fontWeight: 800, color: "goldenrod", display: "inline-block",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                    border: "1px solid rgba(218,165,32,0.25)", letterSpacing: "0.04em",
                }}>
                    C++ LAMBDA EXPRESSIONS
                </Typography>
                <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.45)", fontSize: "0.88rem" }}>
                    syntax · captures · STL algorithms · std::function · higher-order · CRTP · C++20 ranges
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}>
                    {["[capture]","(params)","-> RetType","mutable","std::function","generic auto","constexpr","Ranges"].map(t => (
                        <Chip key={t} label={t} size="small" sx={{
                            bgcolor: "rgba(218,165,32,0.1)", color: "rgba(218,165,32,0.85)",
                            border: "1px solid rgba(218,165,32,0.25)", fontSize: "0.72rem",
                        }} />
                    ))}
                </Box>
            </Box>

            {/* ── Intro Banner ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ bgcolor: "rgba(218,165,32,0.06)", border: "1px solid rgba(218,165,32,0.2)", borderRadius: "14px", p: "18px 24px", display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "1.4rem", flexShrink: 0 }}>💡</Typography>
                    <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.85, color: "rgba(255,255,255,0.8)" }}>
                        <strong style={{ color: "goldenrod" }}>Lambdas</strong> (C++11) are anonymous inline functions
                        that can <strong style={{ color: "goldenrod" }}>capture</strong> variables from their surrounding scope.
                        They are the backbone of modern C++ — used with every STL algorithm, as callbacks,
                        in higher-order functions, and as lightweight closures.
                    </Typography>
                </Box>
            </Box>

            {/* ── Anatomy overview ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <ConceptCard title="Lambda Anatomy" color="goldenrod">
                    <CodeBlock sx={{ "&::before": { content: '""' } }}>{`//  [  capture  ] (  parameters  )  mutable  ->  RetType  {  body  }
//   ─────┬──────   ──────┬───────   ───┬───    ────┬──────   ───┬───
//        │               │             │            │             └── statements
//        │               │             │            └── explicit return type (optional)
//        │               │             └── allows modifying by-value captures
//        │               └── input parameters (like a function)
//        └── variables from outer scope (by value [x] or reference [&x])
//
//  Examples:
//    [](){}                          ← minimal: no capture, no params, empty body
//    [](int x){ return x*2; }        ← parameter, implicit return type
//    [x,&y](int n){ y = x + n; }     ← x by value, y by reference
//    [=](auto a, auto b){ return a+b; } ← generic (C++14), captures all by value`}</CodeBlock>
                </ConceptCard>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>

                    {/* ════════════════════════════════════════════════════
              SECTION 1 — SYNTAX & ANATOMY
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. Syntax &amp; Anatomy
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            A lambda expression creates an unnamed function object at the point of use.
                            It consists of a capture list, optional parameters, an optional return type,
                            and a body. Storing a lambda in <code style={{ color: "goldenrod" }}>auto</code>
                            is always preferred over <code style={{ color: "goldenrod" }}>std::function</code>
                            for local use.
                        </Typography>

                        <StyledTabs value={synTab} onChange={(_, v) => setSynTab(v)} tabs={syntaxTabs.map(t => t.label)} />
                        <CodeBlock>{syntaxTabs[synTab].code}</CodeBlock>

                        {/* Lambda vs function pointer vs functor */}
                        <SectionHeading icon={<AiFillBulb size={18} />} title="Lambda vs Function Pointer vs Functor" subtitle="Three ways to pass behaviour — lambdas are best in most cases" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Form", "Syntax", "Can Capture?", "Inline?", "Best For"]} cols="100px 1.5fr 90px 70px 1.5fr" />}
                            {[
                                { form: "Lambda",          syntax: "[x](int n){ return x+n; }",          cap: "✅ Yes",   inline: "✅ Yes", use: "Default choice — concise and powerful" },
                                { form: "Function ptr",    syntax: "int (*fn)(int) = myFunc;",            cap: "❌ No",    inline: "❌ No",  use: "C API callbacks, simple stateless ops" },
                                { form: "Functor",         syntax: "struct Add{ int op()(int n){...} };", cap: "✅ Yes",   inline: "✅ Yes", use: "Stateful, reusable, pre-C++11" },
                                { form: "std::function",   syntax: "std::function<int(int)> f = ...;",    cap: "✅ Yes",   inline: "❌ No",  use: "Stored callbacks, type-erased containers" },
                                { form: "std::bind",       syntax: "bind(fn, obj, _1)",                   cap: "✅ Yes",   inline: "❌ No",  use: "Legacy — prefer lambda" },
                            ].map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontWeight: 700, color: "goldenrod", fontSize: "0.85rem", mb: 0.4 }}>{row.form}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem", mb: 0.4 }}>{row.syntax}</Typography>
                                        <Typography sx={{ fontSize: "0.78rem", opacity: 0.65 }}>{row.use}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "100px 1.5fr 90px 70px 1.5fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontWeight: 700, color: "goldenrod", fontSize: "0.81rem" }}>{row.form}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{row.syntax}</Box>
                                        <Box sx={{ fontSize: "0.8rem" }}>{row.cap}</Box>
                                        <Box sx={{ fontSize: "0.8rem" }}>{row.inline}</Box>
                                        <Box sx={{ fontSize: "0.79rem", opacity: 0.7 }}>{row.use}</Box>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 2 — CAPTURE MODES
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            2. Capture Modes
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            The capture list controls what variables from the enclosing scope
                            are accessible inside the lambda — and whether they are copied or referenced.
                            Getting captures right is the most important skill for writing safe lambdas.
                        </Typography>

                        <StyledTabs value={capTab} onChange={(_, v) => setCapTab(v)} tabs={captureTabs.map(t => t.label)} />
                        <CodeBlock>{captureTabs[capTab].code}</CodeBlock>

                        {/* Capture reference table */}
                        <SectionHeading icon={<AiFillBulb size={18} />} title="Capture Syntax Reference" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Capture Syntax", "What It Does", "Example"]} cols="130px 2fr 1.5fr" />}
                            {captureRows.map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.9rem", mb: 0.4 }}>{row.syntax}</Typography>
                                        <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.65)", mb: 0.3 }}>{row.desc}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.76rem" }}>{row.example}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "130px 2fr 1.5fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.85rem" }}>{row.syntax}</Box>
                                        <Box sx={{ fontSize: "0.81rem", opacity: 0.75, lineHeight: 1.6 }}>{row.desc}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{row.example}</Box>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 3 — STL ALGORITHMS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            3. Lambdas with STL Algorithms
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Lambdas are the primary way to customise STL algorithms. They replaced
                            verbose custom functor structs and made code like{" "}
                            <code style={{ color: "goldenrod" }}>std::sort</code>,{" "}
                            <code style={{ color: "goldenrod" }}>std::transform</code>, and{" "}
                            <code style={{ color: "goldenrod" }}>std::find_if</code> readable inline.
                        </Typography>

                        <StyledTabs value={stlTab} onChange={(_, v) => setStlTab(v)} tabs={stlTabs.map(t => t.label)} />
                        <CodeBlock>{stlTabs[stlTab].code}</CodeBlock>

                        {/* Algorithm quick-ref */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>⚡</span>} title="Algorithm + Lambda Quick Reference" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 1.5 }}>
                            {[
                                { alg: "std::sort",          lambda: "[](T a, T b){ return a < b; }",           note: "Custom comparator — return true if a comes before b" },
                                { alg: "std::find_if",        lambda: "[](T x){ return condition; }",            note: "Unary predicate — returns first matching element" },
                                { alg: "std::transform",      lambda: "[](T x){ return newX; }",                note: "Unary or binary mapper — one or two input ranges" },
                                { alg: "std::remove_if",      lambda: "[](T x){ return shouldRemove; }",         note: "Pair with .erase() — erase-remove idiom" },
                                { alg: "std::count_if",       lambda: "[](T x){ return condition; }",            note: "Returns count of matching elements" },
                                { alg: "std::accumulate",     lambda: "[](Acc acc, T x){ return acc+x; }",       note: "Binary reducer — fold left over the range" },
                                { alg: "std::all/any/none_of",lambda: "[](T x){ return condition; }",            note: "Returns bool — check predicate against all/any/none" },
                                { alg: "std::for_each",       lambda: "[](T& x){ /* modify x */ }",              note: "Side effects — returns the lambda (C++11)" },
                            ].map((row, j) => (
                                <Box key={j} sx={{ display: "flex", flexDirection: "column", gap: 0.4, bgcolor: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "10px", px: 2, py: "10px" }}>
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.81rem" }}>{row.alg}</Typography>
                                    </Box>
                                    <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.75rem" }}>{row.lambda}</Typography>
                                    <Typography sx={{ fontSize: "0.77rem", opacity: 0.6, lineHeight: 1.5 }}>{row.note}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 4 — std::function & HIGHER-ORDER
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            4. std::function &amp; Higher-Order Functions
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <code style={{ color: "goldenrod" }}>std::function</code> is a type-erased wrapper
                            that stores any callable. Higher-order functions take or return functions —
                            enabling powerful patterns like factories, composition, memoization, and callbacks.
                        </Typography>

                        <StyledTabs value={hiTab} onChange={(_, v) => setHiTab(v)} tabs={higherTabs.map(t => t.label)} />
                        <CodeBlock>{higherTabs[hiTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 5 — ADVANCED PATTERNS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            5. Advanced Lambda Patterns
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Beyond the basics: recursive lambdas, event callbacks, IIFEs for
                            complex initialisation, and <code style={{ color: "goldenrod" }}>constexpr</code>{" "}
                            lambdas evaluated entirely at compile time.
                        </Typography>

                        <StyledTabs value={advTab} onChange={(_, v) => setAdvTab(v)} tabs={advancedTabs.map(t => t.label)} />
                        <CodeBlock>{advancedTabs[advTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              CHEATSHEET
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={3}>
                            Lambda Expressions Cheatsheet
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 3, mb: 4 }}>
                            {cheatBlocks.map((block, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${block.color}33`, borderRadius: "14px", p: "18px 20px" }}>
                                    <Typography sx={{ fontWeight: 800, color: block.color, fontSize: "0.88rem", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{block.title}</Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65 }}>
                                        {block.items.map((item, j) => (
                                            <Typography key={j} sx={{ fontSize: "0.81rem", opacity: 0.82, lineHeight: 1.6 }}>{item}</Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Syntax quick reference */}
                        <Typography sx={{ fontWeight: 800, color: "goldenrod", fontSize: "0.88rem", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Syntax Quick Reference
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                            {[
                                { title: "Basic forms",           color: "#f0883e", code: `auto f1 = []{ return 42; };
auto f2 = [](int x){ return x*2; };
auto f3 = [](auto a, auto b){ return a+b; };
auto f4 = [](int x) -> double { return x; };` },
                                { title: "Captures",              color: "#58a6ff", code: `auto a = [x, y]   { return x+y; };  // by value
auto b = [&x, &y]  { x++; y++; };   // by ref
auto c = [=]       { return x+y; };  // all val
auto d = [&]       { x++; y++; };   // all ref
auto e = [=, &out] { out = x+y; };  // mix` },
                                { title: "std::function storage", color: "#7ee787", code: `using Fn = std::function<int(int)>;
Fn square = [](int x){ return x*x; };
Fn cube   = [](int x){ return x*x*x; };
// swap at runtime:
square = cube;
square(3);   // 27` },
                                { title: "Factory / closure",     color: "#a855f7", code: `auto makeAdder = [](int base) {
  return [base](int x){ return x+base; };
};
auto add10 = makeAdder(10);
auto add20 = makeAdder(20);
add10(5);  // 15
add20(5);  // 25` },
                            ].map((item, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${item.color}33`, borderRadius: "12px", overflow: "hidden" }}>
                                    <Box sx={{ bgcolor: `${item.color}12`, px: 2, py: 1, borderBottom: `1px solid ${item.color}22` }}>
                                        <Typography sx={{ fontWeight: 700, color: item.color, fontSize: "0.82rem" }}>{item.title}</Typography>
                                    </Box>
                                    <CodeBlock sx={{ borderRadius: 0, border: "none", fontSize: "0.79rem", p: "12px 16px", "&::before": { content: '""' } }}>
                                        {item.code}
                                    </CodeBlock>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                </Grid>
            </Grid>
        </PageWrapper>
    );
}
